import { useId } from 'react';
import type { FocusEvent, InputHTMLAttributes, ReactNode } from 'react';

/**
 * Sélectionne toute la valeur à la **prise** de mise au point.
 *
 * 🔴 **Le double appel, dont un différé, n'est pas une précaution : c'est ce
 * qui fait fonctionner le geste sur l'appareil cible.** Safari iOS repositionne
 * le curseur au point touché *après* avoir émis `focus` — une sélection posée
 * synchroniquement seule est écrasée et retombe en simple curseur.
 *
 * ⚠️ Et seulement à la **prise** : toucher un champ déjà actif n'émet aucun
 * `focus`, donc on peut encore placer le curseur à la main pour corriger un
 * seul chiffre. Ne pas « rendre le comportement cohérent » en ajoutant un
 * écouteur sur le clic — ce serait fermer cette porte.
 *
 * ⚠️ Doublon assumé avec `components/ui/primitives.tsx`, où la fonction est
 * privée. Importer depuis l'ancienne bibliothèque ferait dépendre la nouvelle
 * de celle qu'elle remplace ; le doublon disparaît avec elle à la migration.
 */
function selectAllOnFocus(event: FocusEvent<HTMLInputElement>): void {
  const input = event.currentTarget;
  const select = () => {
    if (input.value === '') return;
    try {
      input.setSelectionRange(0, input.value.length);
    } catch {
      /* type d'input sans sélection : rien à faire */
    }
  };
  select();
  requestAnimationFrame(select);
}

/**
 * Champ de saisie — refonte « Nourri ».
 *
 * | Prototype        | Ici                            |
 * |------------------|--------------------------------|
 * | `.field`         | forme par défaut               |
 * | `.search-field`  | `shape="pill"` + `lead={…}`    |
 * | `.macro-input`   | `shape="compact"` + `label`    |
 *
 * 🔴 **La saisie est à 16 px, contre 14 px dans la maquette.** En dessous,
 * Safari iOS zoome à la mise au point quel que soit le `viewport` — verrou
 * posé aux retours de l'étape 5. Les champs sont donc un peu plus grands ici
 * que dans le prototype ; c'est le prix du zoom qui ne part pas.
 *
 * ⚠️ `lead` et `unit` sont décoratifs et masqués aux lecteurs d'écran : une
 * loupe et un « g » décrivent le champ, ils ne le nomment pas. C'est `label`
 * — ou `aria-label` s'il n'y a pas de libellé visible — qui le nomme.
 */
export function TextField({
  label,
  error,
  lead,
  unit,
  shape = 'default',
  numeric = false,
  selectOnFocus = true,
  id,
  className,
  onFocus,
  ...rest
}: {
  /** Libellé visible au-dessus du champ. */
  label?: string;
  /** Message d'erreur, sous le champ. Bascule la bordure sur `--warn`. */
  error?: string;
  /** Ornement de tête — l'icône de la recherche. Décoratif. */
  lead?: ReactNode;
  /** Ornement de queue — « g », « kcal ». Décoratif. */
  unit?: ReactNode;
  shape?: 'default' | 'pill' | 'compact';
  /** Aligne les chiffres (`tabular-nums`) : toute valeur numérique. */
  numeric?: boolean;
  /**
   * Sélectionne toute la valeur à la prise de mise au point — taper remplace.
   * C'est le geste juste quand on **corrige** une valeur, et les champs de ce
   * projet sont presque toujours pré-remplis (retours de l'étape 10).
   */
  selectOnFocus?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & { className?: string }) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;

  const classes = ['n-field'];
  if (shape !== 'default') classes.push(`n-field--${shape}`);
  if (error) classes.push('n-field--invalid');
  if (className) classes.push(className);

  const inputClasses = ['n-field__input'];
  if (numeric) inputClasses.push('n-field__input--numeric');

  return (
    <div className={classes.join(' ')}>
      {label && (
        <label className="n-field__label" htmlFor={inputId}>
          {label}
        </label>
      )}

      {/*
        🔴 **La boîte est un `<label>`, pas un `<div>`.** Mesuré au banc :
        l'`<input>` ne fait que 42 px de haut dans une boîte de 44, si bien que
        la bande restante — bordure et rembourrage — ne réagissait à aucun tap.
        Un `<label>` rend TOUTE la boîte sensible et renvoie la mise au point à
        l'`<input>`, sans une ligne de JavaScript.

        ⚠️ Il ne porte aucun texte propre : l'icône et l'unité sont
        `aria-hidden`, donc le nom accessible reste celui du libellé visible
        (ou de l'`aria-label`). Deux `<label>` sur un même champ sont valides,
        mais un seul doit le nommer.
      */}
      <label className="n-field__box" htmlFor={inputId}>
        {lead && (
          <span className="n-field__lead" aria-hidden="true">
            {lead}
          </span>
        )}

        <input
          id={inputId}
          className={inputClasses.join(' ')}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onFocus={(event) => {
            if (selectOnFocus) selectAllOnFocus(event);
            onFocus?.(event);
          }}
          {...rest}
        />

        {unit && (
          <span className="n-field__unit" aria-hidden="true">
            {unit}
          </span>
        )}
      </label>

      {error && (
        <p className="n-field__error" id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}
